import {
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
  Button,
  Box,
  CircularProgress,
} from '@mui/material';
import RadioButtonCheckedOutlinedIcon from '@mui/icons-material/RadioButtonCheckedOutlined';
import { ErrorOption, FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import { yupResolver } from '@hookform/resolvers/yup';
import { ROUTES, Field, NotificationApi } from '../../../global';
import styles from './styles';
import { LoginFormState } from '../../types';
import useAuthService from '../../hooks/use-auth-service';
import { getLoginValidationSchema } from '../../utils/validation';
import { ResponseStatus } from '../../../global/types';
import { ChangeUserContextType } from '../../contexts/UserContext';
import { AnalyticsService } from '../../../services/analytics.service';

const USER_EVENT = 'userStateChange';

function LoginForm() {
  const { t } = useTranslation('translation', { keyPrefix: 'auth' });
  const formMethods = useForm<LoginFormState>({
    resolver: yupResolver(getLoginValidationSchema()),
    defaultValues: { username: '', password: '', rememberMe: false },
  });

  const { handleSubmit, formState, setError, reset } = formMethods;
  const { login } = useAuthService();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('returnUrl');

  const handleUserStateChange = (event: CustomEvent<ChangeUserContextType>) => {
    const { user } = event.detail;
    if (user) {
      AnalyticsService.trackLogin({
        userId: user.id,
        orgId: user.organization.id,
        orgName: user.organization.name,
        orgHostname: window.location.hostname,
      });
      window.removeEventListener(
        USER_EVENT,
        handleUserStateChange as EventListener,
      );
    }
  };

  const onSubmit = async (values: LoginFormState) => {
    try {
      const response = await login(values);

      if (response.status === ResponseStatus.APPROVED) {
        reset();
        window.addEventListener(
          USER_EVENT,
          handleUserStateChange as EventListener,
        );

        window.dispatchEvent(
          new CustomEvent('forceReauthFromApp', {
            detail: response.data,
          }),
        );
        navigate(returnUrl || ROUTES.monitoring.path);
      } else if (response.errorMessage) {
        setError('form', response.errorMessage as ErrorOption);
        NotificationApi.showError(t('wrongCredentials'));
      }
    } catch {}
  };

  const { form } = formState.errors;

  function navigateToRegister() {
    navigate(ROUTES.auth.forgotPassword.path);
  }

  return (
    <>
      <Typography variant='subtitle1' sx={styles.welcomeBack}>
        {t('welcome')}
      </Typography>
      <>
        <Typography sx={styles.loginTo} variant='h1' whiteSpace='nowrap'>
          {t('loginTo')}
        </Typography>
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container sx={styles.form} spacing={3}>
              <Grid item>
                <Box sx={styles.gridItem}>
                  <Typography
                    color='#4A5568'
                    variant='subtitle1'
                    fontWeight={400}
                    component='label'
                  >
                    {t('email')}
                  </Typography>
                  <Field name='username'>
                    <TextField
                      size='medium'
                      fullWidth
                      inputProps={{ 'aria-label': 'Email' }}
                    />
                  </Field>
                </Box>
              </Grid>
              <Grid item>
                <Box sx={styles.gridItem}>
                  <Typography
                    color='#4A5568'
                    variant='subtitle1'
                    fontWeight={400}
                    component='label'
                  >
                    {t('password')}
                  </Typography>
                  <Field name='password'>
                    <TextField
                      size='medium'
                      type='password'
                      fullWidth
                      inputProps={{ 'aria-label': 'Password' }}
                      error={!!formState.errors.password}
                      helperText={formState.errors.password?.message}
                    />
                  </Field>
                </Box>
              </Grid>
              <Box sx={styles.footer}>
                <Grid item>
                  <FormControlLabel
                    control={
                      <Field name='rememberMe' attachError={false}>
                        <Checkbox
                          sx={styles.checkbox}
                          size='small'
                          icon={
                            <CircleOutlinedIcon
                              sx={styles.icon}
                              color='disabled'
                            />
                          }
                          checkedIcon={
                            <RadioButtonCheckedOutlinedIcon
                              color='primary'
                              sx={styles.icon}
                            />
                          }
                        />
                      </Field>
                    }
                    label={t('remember')}
                    sx={styles.rememberMe}
                  />
                </Grid>
                <Button sx={styles.forgot} onClick={navigateToRegister}>
                  {t('forgot')}
                </Button>
              </Box>
              {form && <Typography>{form?.message}</Typography>}
              <Grid sx={styles.loginButton} item>
                <Button
                  fullWidth
                  variant='contained'
                  size='large'
                  disabled={formState.isSubmitting}
                  type='submit'
                  startIcon={
                    formState.isSubmitting ? (
                      <CircularProgress size={20} />
                    ) : null
                  }
                >
                  {formState.isSubmitting ? t('loggingIn') : t('login')}
                </Button>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </>
    </>
  );
}

export default LoginForm;
